/*
  File: EnvBanner.tsx
  Path: src/components/EnvBanner.tsx
  Description: Full-width environment ribbon for non-production builds.
               local -> indigo "LOCAL DEV"; staging -> amber "STAGING";
               production -> renders nothing. Also prefixes the browser tab title
               and swaps the favicon per environment so background tabs are clear.
  Author: Larry McLean + AI Team
  Version: 1.0.0
  Last Updated: 2026-08-18

  Notes:
    - Environment comes from src/utils/env.ts (single source of truth).
    - Fully self-contained inline styles; does NOT touch themes.css / dashboard.css.
    - In-flow element: pushes the page down a few px on test only; renders null on
      production, so Live has zero footprint and no layout change.

  Change Log:
    - 2026-08-18 - v1.0.0 (Larry McLean + AI Team)
      - Initial creation: three-state environment banner (local / staging / production)
        with tab-title prefix and per-environment favicon dot.
*/
import { useEffect } from "react";
import { getAqxEnv, type AqxEnv } from "../utils/env";

type Look = { label: string; message: string; bg: string; fg: string };

const LOOK: Record<Exclude<AqxEnv, "production">, Look> = {
  local: {
    label: "LOCAL DEV",
    message: "Local development — running on your own machine. Not staging, not live.",
    bg: "#4F46E5", // indigo-600
    fg: "#FFFFFF",
  },
  staging: {
    label: "STAGING",
    message: "This is your playground. Nothing you do here affects your live site.",
    bg: "#F59E0B", // amber-500
    fg: "#1F2937",
  },
};

export default function EnvBanner() {
  const env = getAqxEnv();
  const look = env === "production" ? null : LOOK[env];

  useEffect(() => {
    if (!look) return; // production: leave tab + favicon untouched

    const PREFIX = `[${look.label.split(" ")[0]}] `; // [LOCAL] or [STAGING]
    if (!document.title.startsWith(PREFIX)) document.title = PREFIX + document.title;

    const dot =
      "data:image/svg+xml," +
      encodeURIComponent(
        `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><circle cx="16" cy="16" r="14" fill="${look.bg}"/></svg>`
      );
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    const prev = link?.getAttribute("href") || null;
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.setAttribute("href", dot);

    return () => {
      if (prev && link) link.setAttribute("href", prev);
    };
  }, [look]);

  if (!look) return null; // production renders nothing

  return (
    <div
      role="status"
      aria-label={`${look.label} environment`}
      style={{
        width: "100%",
        boxSizing: "border-box",
        background: look.bg,
        color: look.fg,
        font: '700 13px/1.3 system-ui, -apple-system, "Segoe UI", sans-serif',
        letterSpacing: "0.02em",
        padding: "7px 14px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "10px",
        textAlign: "center",
      }}
    >
      <span
        style={{
          fontWeight: 800,
          fontSize: "11px",
          padding: "1px 8px",
          borderRadius: "999px",
          background: look.fg === "#FFFFFF" ? "rgba(255,255,255,0.18)" : "rgba(31,41,55,0.14)",
        }}
      >
        {look.label}
      </span>
      <span>{look.message}</span>
    </div>
  );
}